import { FormControl, InputLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material'
import { SUBSCRIPTION_PLANS, getPlanByType } from 'features/Subscription/constants'
import { usePublicConfig } from 'hooks/usePublicConfig'
import { SubscriptionPlan, SubscriptionPlanType } from 'types'

export interface SubscriptionPlanSelectProps {
  value: SubscriptionPlanType | ''
  onChange: (value: SubscriptionPlanType) => void
  disabled?: boolean
  /** When set, this plan cannot be chosen (e.g. already scheduled as the next period). */
  disabledPlanType?: SubscriptionPlanType | null
}

function formatPlanOption(plan: SubscriptionPlan): string {
  const amount =
    plan.period === 'month' || plan.period === 'year'
      ? `$${plan.price}/${plan.period}`
      : `$${plan.price} USD`
  const seatsSuffix = plan.maxSeats ? `, up to ${plan.maxSeats} users` : ''
  return `${plan.label} — ${amount}${seatsSuffix}`
}

export function SubscriptionPlanSelect({
  value,
  onChange,
  disabled = false,
  disabledPlanType,
}: SubscriptionPlanSelectProps) {
  const { config } = usePublicConfig()
  const plans = SUBSCRIPTION_PLANS.map((plan) => {
    const remote = config?.plans?.[plan.type]
    if (!remote) return plan
    return {
      ...plan,
      price: remote.price,
      period: remote.interval,
      maxSeats: remote.maxSeats ?? plan.maxSeats,
    }
  })
  const currentPlan = value ? getPlanByType(value) : undefined
  const showCurrentLegacyPlan =
    !!currentPlan && !plans.some((plan) => plan.type === currentPlan.type)

  const handleChange = (event: SelectChangeEvent) => {
    onChange(event.target.value as SubscriptionPlanType)
  }

  return (
    <FormControl fullWidth size="small">
      <InputLabel>Select Subscription Plan</InputLabel>
      <Select
        value={value}
        label="Select Subscription Plan"
        onChange={handleChange}
        disabled={disabled}
      >
        {showCurrentLegacyPlan && currentPlan && (
          <MenuItem value={currentPlan.type} disabled>
            {formatPlanOption(currentPlan)}
          </MenuItem>
        )}
        {plans.map((plan) => {
          const isScheduledNext = disabledPlanType != null && plan.type === disabledPlanType
          return (
            <MenuItem
              key={plan.type}
              value={plan.type}
              disabled={isScheduledNext}
              title={
                isScheduledNext
                  ? 'This plan is already scheduled to start on your next renewal.'
                  : undefined
              }
            >
              {formatPlanOption(plan)}
              {isScheduledNext ? ' — already scheduled' : ''}
            </MenuItem>
          )
        })}
      </Select>
    </FormControl>
  )
}
